import { useState, useEffect, useMemo } from "react";
import { API, Auth } from "aws-amplify";
import {
  Heading,
  Loader,
  Tabs,
  TabItem,
  Link,
  View,
} from "@aws-amplify/ui-react";
import { useNavigate, useLocation } from "react-router-dom";

import Name from "./Name";
import Audience from "./Audience";
import Content from "./Content";
import Ongoing from "./Ongoing";

export function TargetedCampaign({
  campaignId,
  balance,
  updateBalance,
  subscription,
  clientData,
  profiles,
  updateProfiles,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // Tabs & routing
  const [activeTab, setActiveTab] = useState(0);

  // Campaign states
  const [campaignName, setCampaignName] = useState("");
  const [campaignRecord, setCampaignRecord] = useState(null);
  const [selectedProfiles, setSelectedProfiles] = useState([]);  
  // eslint-disable-next-line no-unused-vars
  const [audience, setAudience] = useState("");
  const [keywords, setKeywords] = useState("");

  // Connection tasks
  const [connections, setConnections] = useState([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [justUpdatedAudience, setJustUpdatedAudience] = useState(false);

  // Determine if new or existing campaign
  const isNewCampaign = !campaignId;

  // Load campaign if campaignId exists
  useEffect(() => {
    async function getCampaign() {
      setLoading(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        const request = {
          headers: {
            Authorization: `${user.signInUserSession.idToken.jwtToken}`,
          },
          queryStringParameters: {
            userId: user.username,
            slug: campaignId,
            tasks: true,
            profiles: true,
          },
        };
        const resp = await API.get("gcAPI", "/get-targetedCampaign", request);
        setCampaignName(resp.campaignName || "");
        setCampaignRecord(resp);
        setSelectedProfiles(resp.profiles || []);
        setConnections(resp.tasks || []);
      } catch (e) {
        console.error("Error loading campaign", e);
      }
      setLoading(false);
    }
    if (campaignId) {
      getCampaign();
    }
  }, [campaignId]);

  // Delayed fetch of connection tasks after updating audience
  useEffect(() => {
    if (!campaignId) return;

    let timerId;
    async function fetchConnections() {
      setIsLoadingConnections(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        const request = {
          headers: {
            Authorization: `${user.signInUserSession.idToken.jwtToken}`,
          },
          queryStringParameters: {
            userId: user.username,
            slug: campaignId,
            tasks: true,
            profiles: true,
          },
        };
        const resp = await API.get("gcAPI", "/get-targetedCampaign", request);
        setConnections(resp.tasks || []);
        setSelectedProfiles(resp.profiles || []);
      } catch (error) {
        console.error("Error fetching connections:", error);
      }
      setIsLoadingConnections(false);
    }

    if (justUpdatedAudience) {
      timerId = setTimeout(() => {
        fetchConnections();
        setJustUpdatedAudience(false);
      }, 5000);
    } else {
      fetchConnections();
    }

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [campaignId, justUpdatedAudience]);

  // Read ?tab=N from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam !== null) {
      setActiveTab(parseInt(tabParam, 10));
    } else if (!isNewCampaign) {
      // If no profiles, open CONTENT (2), else open ONGOING (3)
      if (!selectedProfiles?.length) {
        setActiveTab(2);
      } else {
        setActiveTab(3);
      }
    } else {
      setActiveTab(0);
    }
  }, [location, isNewCampaign, selectedProfiles]);

  // Onboarding checks
  const isOnboarding = !!campaignRecord?.onboarding;
  const isAudienceEmpty = useMemo(() => !audience?.trim(), [audience]);
  const computedHeadingText =
    campaignRecord?.onboarding || isNewCampaign
      ? "3 Steps to Create a New Targeted Campaign"
      : "Update Targeted Campaign";
  const finalHeadingText =
    !isNewCampaign && activeTab === 0
      ? "Update Targeted Campaign"
      : computedHeadingText;

  // Tab enabling logic
  const isTab2Disabled = isNewCampaign;
  let isTab3Disabled = isNewCampaign || (isOnboarding && isAudienceEmpty);
  if (!isNewCampaign && campaignRecord?.onboarding && connections?.length > 0) {
    isTab3Disabled = false;
  }

  const isTab4Disabled =
    isNewCampaign || isTab3Disabled || (
    isOnboarding && isAudienceEmpty && !connections?.length && !selectedProfiles?.length &&
      //profiles.length = 0 or no validated profiles
      (!profiles || !profiles.length || profiles.every((p) => !p.validated)) 
      
    );

  // LinkedIn Auth for targeted campaign
  const linkedInAuthUrl = async () => {
    const user = await Auth.currentAuthenticatedUser();
    const userId = user.username;
    const clientId = "77dyuiqhdxf7a5"; // your LinkedIn app client ID
    const redirectUri = encodeURIComponent(
      "https://api.1stinfluencer.com/prod/social-auth/linkedin"
    );
    let requestIP = "";
    try {
      requestIP = await fetch("https://ifconfig.me/ip").then((r) => r.text());
      if (requestIP.length > 15 || requestIP.length < 7) requestIP = "";
    } catch (e) {
      //console.log("IP error: ", e.message);
    }
    // For targeted campaign add a pipe plus campaignId in state
    let stateParam = "";
    if (campaignId) {
      stateParam = `${requestIP}-${userId}|${campaignId}`;
    } else {
      stateParam = `${requestIP}-${userId}`;
    }
    const scope = encodeURIComponent(
      "r_basicprofile r_emailaddress r_1st_connections_size"
    );
    return `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${stateParam}&scope=${scope}`;
  };

  // Handle Submit
  async function handleSubmit(action, extraData = {}) {
    setLoading(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      const reqBody = { action, ...extraData };

      if (action === "create") {
        reqBody.campaignName = campaignName;
      }
      if ((action === "update" || action === "checkout") && campaignRecord) {
        reqBody.campaignId = campaignRecord.SK;
        if (campaignName) reqBody.campaignName = campaignName;
        if (extraData.content) {
          reqBody.profiles = extraData.profiles;
          reqBody.influencers = extraData.influencers;
          reqBody.contentManagement = extraData.contentManagement;
        }
      }
      if (action === "update") {
        extraData.keywords = keywords;
      }

      const request = {
        headers: {
          Authorization: `${user.signInUserSession.idToken.jwtToken}`,
        },
        body: reqBody,
      };
      const resp = await API.post(
        "gcAPI",
        "/post-updateTargetedCampaign",
        request
      );
      setCampaignRecord(resp);

      if (action === "update" && (extraData.companies || extraData.connections)) {
        setJustUpdatedAudience(true);
      }

      if (action === "create") {
        setActiveTab(1);
        navigate(
          `/updatetargetedcampaign/${resp.SK.replace("TARGETEDCAMPAIGN ", "")}?tab=1`
        );
      } else if (action === "update") {
        if (extraData.audience) {
          setActiveTab(2);
        } else if (extraData.content || extraData.profiles) {
          setActiveTab(3);
        }
      } else if (action === "checkout") {
        navigate("/tasks");
      }
    } catch (e) {
      console.error("Submission error:", e);
    }
    setLoading(false);
  }

  if (loading && campaignId) {
    return <Loader variation="linear" />;
  }

  if (!isNewCampaign && (!campaignRecord || !campaignRecord.SK)) {
    return <Heading level={3}>No data or campaign not found</Heading>;
  }

  return (
      <>
          <Heading level={3} textAlign="center">
              {finalHeadingText}
          </Heading>
          <br />
          <Tabs
              spacing="equal"
              currentIndex={activeTab}
              onChange={(e) => setActiveTab(e)}
          >
              <TabItem title="1. NAME" isDisabled={false}>
                  <Name
                      clientData={clientData}
                      campaignName={campaignName}
                      setCampaignName={setCampaignName}
                      campaignId={campaignId}
                      handleSubmit={handleSubmit}
                      loading={loading}
                      navigate={navigate}
                  />
              </TabItem>

              <TabItem title="2. AUDIENCE" isDisabled={isTab2Disabled}>
                  <Audience
                      clientData={clientData}
                      campaignRecord={campaignRecord}
                      handleSubmit={handleSubmit}
                      keywords={keywords}
                      setKeywords={setKeywords}
                      connections={connections}
                      isLoadingConnections={isLoadingConnections}
                      navigate={navigate}
                  />
              </TabItem>

              <TabItem title="3. CONTENT" isDisabled={isTab3Disabled}>
                  <Content
                      clientData={clientData}
                      subscription={subscription}
                      profiles={profiles}
                      selectedProfiles={selectedProfiles}
                      setSelectedProfiles={setSelectedProfiles}
                      navigate={navigate}
                      linkedInAuthUrl={linkedInAuthUrl}
                  />
              </TabItem>

              <TabItem title="ONGOING" isDisabled={isTab4Disabled}>
                  <Ongoing
                      clientData={clientData}
                      handleSubmit={handleSubmit}
                      loading={loading}
                  />
              </TabItem>
          </Tabs>
          <View align="center">
              <br />
              <Link href="/campaigns">
                  <u>Back to Campaigns</u>
              </Link>
          </View>{" "}
      </>
  );
}
